import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const ExitIntentPopup = () => {
  const { user, subscription } = useAuth();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const isPremium = subscription?.subscribed;

  const handleStartTrial = () => {
    if (user) {
      navigate('/pricing');
    } else {
      navigate('/auth');
    }
    setIsVisible(false);
  };

  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShown && !isPremium) {
        setIsVisible(true);
        setHasShown(true);
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [hasShown, isPremium]);

  if (!isVisible || isPremium) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="relative bg-card border border-primary/20 rounded-lg p-8 max-w-lg mx-4 shadow-glow-primary animate-scale-in">
        <button
          onClick={() => setIsVisible(false)}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close popup"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="space-y-6 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-primary rounded-full mb-4">
            <span className="text-lg font-bold">🎁 Wait! Special Offer</span>
          </div>
          
          <h3 className="text-3xl font-bold">
            Don't Miss Out on Your <span className="bg-gradient-primary bg-clip-text text-transparent">Free Trial</span>
          </h3>
          
          <p className="text-lg text-muted-foreground">
            Start trading like a pro with our 7-day free trial. No credit card required!
          </p>

          <div className="space-y-3 text-left bg-background/50 rounded-lg p-4">
            <p className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Access to all professional trading signals</span>
            </p>
            <p className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Live market insights from expert traders</span>
            </p>
            <p className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Risk management strategies</span>
            </p>
          </div>

          <Button 
            size="lg" 
            className="w-full bg-gradient-primary hover:shadow-glow-primary transition-all duration-300 text-lg py-6"
            onClick={handleStartTrial}
          >
            {user ? "Get Premium — Start Free Trial" : "Sign Up — Start Free Trial"}
          </Button>

          <p className="text-sm text-muted-foreground">
            Join hundreds of traders already profiting with Oraculus:X
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
